import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  loadPdfBrandAssets,
  PDF_BRAND_FOOTER_MARGIN,
  pdfBrandDidDrawPage,
} from "../utils/pdfBrand";
import TransactionsViewer from "../components/TransactionsViewer";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import FeeVerificationModal from "../components/FeeVerificationModal";
import NotificationContext from "../context/NotificationContext";
import UserCoursesModal from "../components/UserCoursesModal";
import { REGISTRATION_UPLOAD_SAMPLE } from "../utils/csvTemplates";
import BulkCsvControls from "../components/BulkCsvControls";
import { downloadCsv } from "../utils/csv";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { isSafeFeeProofDisplayUrl } from "../utils/feeProofUpload";
import { formatDisplayDate } from "../utils/dateFormat";
import { useMutationLock } from "../hooks/useMutationLock";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import PaginationFooter from "../components/PaginationFooter";
import AppTooltip from "../components/AppTooltip";
import {
  DEFAULT_PAGE_SIZE,
  normalizeListResponse,
} from "../utils/listPagination";
import * as registrationsApi from "../api/registrations";
import { BULK_UPLOAD_ENDPOINT as REGISTRATION_BULK_UPLOAD_ENDPOINT } from "../api/registrations";
import * as coursesApi from "../api/courses";
import * as feeTransactionsApi from "../api/feeTransactions";

export default function AdminRegistrations() {
  const [data, setdata] = useState([]);
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_registrations");
  const { hasPermission: canEdit } = useViewPermission("edit_registrations");
  const { run: runVerifyFee } = useMutationLock();
  const { busy: fieldSaving, run: runFieldEdit } = useMutationLock();

  const [filters, setFilters] = useState({
    name: "",
    email: "",
    courseId: "",
    feeMode: "",
    paymentStatus: "",
    balance: "",
    requiresVerification: false,
  });
  const [columnFilters, setColumnFilters] = useState({});
  const [allcourses, setallCourses] = useState([]);
  const [filterstartDate, setFilterStartDate] = useState("");
  const [filterendDate, setFilterEndDate] = useState("");
  const [appliedDateRange, setAppliedDateRange] = useState({
    fromDate: "",
    toDate: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const limit = DEFAULT_PAGE_SIZE;
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const debouncedName = useDebouncedValue(filters.name, 400);
  const debouncedEmail = useDebouncedValue(filters.email, 400);
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 400);

  const listFilters = useMemo(() => {
    const next = {
      name: debouncedName || undefined,
      courseId: filters.courseId || undefined,
      feeMode: filters.feeMode || undefined,
      paymentStatus: filters.paymentStatus || undefined,
      balance: filters.balance || undefined,
      requiresVerification: filters.requiresVerification ? "true" : undefined,
      fromDate: appliedDateRange.fromDate || undefined,
      toDate: appliedDateRange.toDate || undefined,
    };
    Object.entries(debouncedColumnFilters || {}).forEach(([key, value]) => {
      if (value) next[key] = value;
    });
    return next;
  }, [
    debouncedName,
    filters.courseId,
    filters.feeMode,
    filters.paymentStatus,
    filters.balance,
    filters.requiresVerification,
    debouncedColumnFilters,
    appliedDateRange.fromDate,
    appliedDateRange.toDate,
  ]);

  useEffect(() => {
    if (!hasPermission) return;
    const fetchCourses = async () => {
      try {
        const response = await coursesApi.list({
          exportMode: true,
          filters: { active: true },
        });
        setallCourses(normalizeListResponse(response.data).data);
      } catch (err) {
        console.error("Error fetching courses:", err);
      }
    };
    fetchCourses();
  }, [hasPermission]);

  function getCourseTitle(courseId) {
    const course = allcourses.find((course) => course.id === courseId);
    return course ? course.title : "Course title not found";
  }

  const parseCourseFee = (fee) => {
    const parsed = parseInt(String(fee || "").replace(/[^0-9]/g, ""), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const parseCourseDiscount = (discount) => {
    const parsed = parseFloat(String(discount || "0").replace(/[^0-9.]/g, ""));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const hasPendingEnrollment = (registration) =>
    (registration.courses || []).some(
      (course) => course.status === "pending",
    );

  const formatRegistrationPaymentStatus = (registration) => {
    if (hasPendingEnrollment(registration)) return "Pending enrollment";
    if (registration.paymentstatus === "partially_paid") return "Partially Paid";
    return registration.paymentstatus || "";
  };

  /** Same active-course / fee / status / balance rules as the registrations table. */
  const buildRegistrationExportRow = (registration) => {
    const activeCourses = (registration.courses || []).filter(
      (course) => course.status === "enrolled" || course.status === "pending",
    );
    const monthlyFee = activeCourses.reduce((acc, curr) => {
      const fee = parseCourseFee(curr.fee);
      const courseDiscount = parseCourseDiscount(curr.discount);
      return acc + Math.max(0, Math.round(fee - courseDiscount));
    }, 0);
    const owed = Number(registration.owedTotal) || 0;
    const credit = Number(registration.creditTotal) || 0;
    const paymentStatus = formatRegistrationPaymentStatus(registration);
    const balanceNote =
      credit > 0
        ? owed > 0
          ? `Owed ${owed.toLocaleString()} · Credit ${credit.toLocaleString()}`
          : `Credit PKR ${credit.toLocaleString()}`
        : "";

    return {
      id: registration.id,
      userId: registration.userId,
      name: registration.name || "",
      email: registration.email || "",
      phone: registration.phone || "",
      parentsPhoneNumber: registration.parentsPhoneNumber || "",
      city: registration.city || "",
      courses: activeCourses.map((course) => course.title).join(", "),
      fee: `PKR ${monthlyFee.toLocaleString()} /month`,
      feemode: registration.feemode || "Not Set",
      paymentstatus: paymentStatus,
      balance: registration.balance || "PKR 0",
      owedTotal: owed,
      creditTotal: credit,
      balanceDetail: balanceNote,
      feeProofs: (registration.feePaymentProofs || []).length,
      duedate: registration.duedate
        ? formatDisplayDate(registration.duedate, "")
        : "",
      comments: registration.comments || "",
    };
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
    }));
    if (filterName !== "email" && filterName !== "name") {
      setCurrentPage(1);
    }
  };

  const togglePendingVerification = () => {
    if (filters.requiresVerification) {
      setFilters({
        name: "",
        email: "",
        courseId: "",
        feeMode: "",
        paymentStatus: "",
        balance: "",
        requiresVerification: false,
      });
    } else {
      setFilters({
        name: "",
        email: "",
        courseId: "",
        feeMode: "",
        paymentStatus: "",
        balance: "",
        requiresVerification: true,
      });
    }
    setColumnFilters({});
    setFilterStartDate("");
    setFilterEndDate("");
    setAppliedDateRange({ fromDate: "", toDate: "" });
    setCurrentPage(1);
  };

  const fetchRegistrations = useCallback(
    async (pageArg = 1) => {
      try {
        if (!initialLoading) setRefreshing(true);
        const response = await registrationsApi.list({
          page: pageArg,
          limit,
          search: debouncedEmail,
          filters: listFilters,
        });
        const normalized = normalizeListResponse(response.data, pageArg, limit);
        const updatedData = normalized.data.map((item) => ({
          ...item,
          courseId: getCourseTitle(item.courseId) || item.courseId,
        }));
        setdata(updatedData);
        setCurrentPage(normalized.page);
        setTotal(normalized.total);
        setTotalPages(normalized.totalPages);
      } catch (error) {
        console.error(
          "Error:",
          error.response ? error.response.data : error.message,
        );
        setdata([]);
        setTotal(0);
        setTotalPages(0);
        notificationCtx.show({
          message: "Failed to fetch registrations.",
          stat: "error",
        });
      } finally {
        setInitialLoading(false);
        setRefreshing(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [limit, debouncedEmail, listFilters, allcourses, initialLoading],
  );

  useEffect(() => {
    if (!hasPermission) return;
    setCurrentPage(1);
    fetchRegistrations(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, debouncedEmail, listFilters]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchRegistrations(newPage);
  };

  const fetchExportRows = async () => {
    const response = await registrationsApi.list({
      page: 1,
      exportMode: true,
      search: debouncedEmail,
      filters: listFilters,
    });
    return normalizeListResponse(response.data).data;
  };

  const handleInputChange = (id, field, value) => {
    setdata((prevRegistrations) =>
      prevRegistrations.map((registration) =>
        registration.id === id
          ? { ...registration, [field]: value }
          : registration,
      ),
    );
  };

  const handleEdit = async (id, field, value) => {
    if (!canEdit) return;
    await runFieldEdit(async () => {
      try {
        await registrationsApi.update(id, { field, value });
        setdata((prevRegistrations) =>
          prevRegistrations.map((registration) =>
            registration.id === id
              ? { ...registration, [field]: value }
              : registration,
          ),
        );
        notificationCtx.show({
          message: "Registration updated successfully!",
          stat: "success",
        });
      } catch (error) {
        console.error("Error updating registration:", error);
        notificationCtx.show({
          message: "Failed to update the registration.",
          stat: "error",
        });
      }
    });
  };

  const downloadPDF = async () => {
    try {
      const rows = (await fetchExportRows()).map(buildRegistrationExportRow);
      const doc = new jsPDF({ orientation: "landscape" });
      const marginX = 14;
      const assets = await loadPdfBrandAssets(doc);
      const tableColumn = [
        "Registration ID",
        "Name",
        "Email",
        "Phone",
        "Parent Phone",
        "City",
        "Courses",
        "Fee",
        "Fee Mode",
        "Payment Status",
        "Balance",
        "Owed",
        "Credit",
        "Balance Detail",
        "Fee Proofs",
        "Due Date",
        "Comments",
      ];
      const tableRows = rows.map((row) => [
        row.id,
        row.name,
        row.email,
        row.phone,
        row.parentsPhoneNumber,
        row.city,
        row.courses,
        row.fee,
        row.feemode,
        row.paymentstatus,
        row.balance,
        row.owedTotal,
        row.creditTotal,
        row.balanceDetail,
        row.feeProofs,
        row.duedate,
        row.comments,
      ]);

      let cursorY = assets.headerH + 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.setTextColor(48, 45, 69);
      doc.text("Registrations", marginX, cursorY);
      cursorY += 8;

      doc.autoTable({
        startY: cursorY,
        head: [tableColumn],
        body: tableRows,
        margin: {
          top: assets.headerH + 6,
          left: marginX,
          right: marginX,
          bottom: PDF_BRAND_FOOTER_MARGIN,
        },
        didDrawPage: pdfBrandDidDrawPage(
          doc,
          assets,
          "registration records",
          marginX,
        ),
        headStyles: { fillColor: [213, 204, 253], textColor: [64, 59, 85] },
        styles: { fontSize: 8 },
      });
      doc.save("registration_table.pdf");
    } catch (error) {
      console.error("Export PDF failed:", error);
      notificationCtx.show({ message: "Failed to export PDF.", stat: "error" });
    }
  };

  const downloadCsvExport = async () => {
    try {
      const rows = (await fetchExportRows()).map(buildRegistrationExportRow);
      const csvRows = rows.map((row) => ({
        id: row.id,
        userId: row.userId,
        name: row.name,
        email: row.email,
        phone: row.phone,
        parentsPhoneNumber: row.parentsPhoneNumber,
        city: row.city,
        courses: row.courses,
        fee: row.fee,
        feemode: row.feemode,
        paymentstatus: row.paymentstatus,
        balance: row.balance,
        owedTotal: row.owedTotal,
        creditTotal: row.creditTotal,
        balanceDetail: row.balanceDetail,
        feeProofs: row.feeProofs,
        duedate: row.duedate,
        comments: row.comments,
      }));
      downloadCsv("registration_table.csv", csvRows);
    } catch (error) {
      console.error("Export CSV failed:", error);
      notificationCtx.show({ message: "Failed to export CSV.", stat: "error" });
    }
  };

const [columnWidths, setColumnWidths] = useState({});
  const MIN_WIDTH = 50; // Minimum column width in pixels

  const handleResize = (e, col) => {
    const startX = e.clientX;
    const startWidth = columnWidths[col] || 100; // Default to 100px if not set

    const handleMouseMove = (event) => {
      const delta = event.clientX - startX;
      const newWidth = Math.max(MIN_WIDTH, startWidth + delta);

      setColumnWidths((prevWidths) => ({
        ...prevWidths,
        [col]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const [editingFeeMode, setEditingFeeMode] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  const [editingComment, setEditingComment] = useState(false);
  const [commentValue, setCommentValue] = useState("");

  const columnKeys = [
    // "id",
    // "userId",
    // "name",
    // "email",
    // "phone",
    // "city",
    "userinfo",
    "courses",
    "feemode",
    "fee",
    "paymentstatus",
    "balance",
    "feePaymentProofs",
    // "paymentdates",
    "transactions",
    "duedate",
    "comments",
  ];

  const columnHeaderLabel = (key) => {
    if (key === "feePaymentProofs") return "FEE PROOFS";
    if (key === "userinfo") return "USER INFO";
    if (key === "feemode") return "FEE MODE";
    if (key === "paymentstatus") return "PAYMENT STATUS";
    if (key === "duedate") return "DUE DATE";
    return key.toUpperCase();
  };

  const [selectedRegistration, setSelectedRegistration] = useState("");
  const [showfeeImageModal, setShowfeeImageModal] = useState(false);
  const [isExistingPayment, setIsExistingPayment] = useState(false);
  const [selectedProofId, setSelectedProofId] = useState(null);
  const [selectedFeeTransactionId, setSelectedFeeTransactionId] =
    useState(null);
  const [paymentDate, setPaymentDate] = useState("");
  const [feeimage, setFeeImage] = useState("");

  const [amountPaid, setAmountPaid] = useState("");
  const [transactionId, setTransactionId] = useState("");

  async function verifyFeePayment(feeItems) {
    if (!canEdit) return;
    await runVerifyFee(async () => {
      const confirmPayment = async () => {
        try {
          const transactionDetails = {
            transactionId,
            registrationId: selectedRegistration.id,
            amountPaid,
            paymentDate,
          };
          if (feeItems?.length) transactionDetails.feeItems = feeItems;
          if (selectedProofId)
            transactionDetails.feePaymentProofId = selectedProofId;
          const response = await feeTransactionsApi.create(transactionDetails);
          if (response.data.message === "Transaction Recorded Successfully") {
            notificationCtx.show({
              message: "Fee payment verified successfully!",
              stat: "success",
            });
            setShowfeeImageModal(false);
            await fetchRegistrations();
          }
        } catch (error) {
          console.error("Error updating registration:", error);
          if (error.response?.data?.errorCode === "DUPLICATE_TRANSACTION") {
            const { existingRegistrationId, existingPaymentDate } =
              error.response.data;
            notificationCtx.show({
              message: `A duplicate transaction id exists for ${existingRegistrationId} with payment date ${formatDisplayDate(existingPaymentDate)}.`,
              stat: "error",
            });
          } else {
            notificationCtx.show({
              message: "Failed to verify fee payment.",
              stat: "error",
            });
          }
        }
      };
      const owedCompare =
        selectedRegistration.owedTotal != null
          ? Math.round(Number(selectedRegistration.owedTotal) || 0)
          : Math.max(
              0,
              parseInt(
                String(selectedRegistration.balance ?? "").replace(
                  /[^0-9-]/g,
                  "",
                ),
                10,
              ) || 0,
            );
      if (parseInt(amountPaid, 10) !== owedCompare) {
        notificationCtx.show({
          message: (
            <>
              <div style={{ fontWeight: "600", marginBottom: "8px" }}>
                <strong>Payment Amount Mismatch</strong>
              </div>
              <div
                style={{
                  lineHeight: "1.5",
                  fontSize: "14px",
                  textAlign: "left",
                  fontWeight: 400,
                }}
              >
                <div style={{ marginBottom: "10px" }}>
                  You are about to record a payment that differs from the
                  amount currently owed.
                </div>
                <div style={{ marginBottom: "10px" }}>
                  <div>
                    Amount Owed:{" "}
                    <strong>PKR {owedCompare.toLocaleString()}</strong>
                  </div>
                  <div>
                    Entered Amount: <strong>PKR {amountPaid}</strong>
                  </div>
                </div>
                <div>
                  Click OK to proceed anyway, or Cancel to adjust the amount.
                </div>
              </div>
            </>
          ),
          stat: "error",
          onConfirm: confirmPayment,
        });
      } else {
        await confirmPayment();
      }
    });
  }

  const [transactionData, setTransactionData] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showFeeImagesModal, setShowFeeImagesModal] = useState(false);
  const [feeImagesModalData, setFeeImagesModalData] = useState(null);
  const [proofAction, setProofAction] = useState(null);
  const [proofActionNote, setProofActionNote] = useState("");
  const [proofLinkTxnId, setProofLinkTxnId] = useState("");
  const [proofLinkOptions, setProofLinkOptions] = useState([]);
  const [proofActionBusy, setProofActionBusy] = useState(false);
  const [showAllCoursesModal, setShowAllCoursesModal] = useState(false);
  const [allCoursesModalData, setAllCoursesModalData] = useState(null);

  const resolveProofStatus = (proof) => {
    if (proof?.status) return proof.status;
    if (proof?.feeTransactionId) return "verified";
    return "pending";
  };

  const proofStatusStyle = (status) => {
    switch (status) {
      case "verified":
        return { backgroundColor: "#bff1db", color: "#1f4d3a" };
      case "linked":
        return { backgroundColor: "#d5d1fd", color: "#3a3560" };
      case "dismissed":
        return { backgroundColor: "#ebe8f2", color: "#5a5568" };
      default:
        return { backgroundColor: "#f9e1be", color: "#5a4020" };
    }
  };

  const resetProofAction = () => {
    setProofAction(null);
    setProofActionNote("");
    setProofLinkTxnId("");
    setProofLinkOptions([]);
    setProofActionBusy(false);
  };

  const applyProofUpdate = (updatedProof) => {
    if (!updatedProof?.id) return;
    setFeeImagesModalData((prev) =>
      (prev || []).map((proof) =>
        proof.id === updatedProof.id ? { ...proof, ...updatedProof } : proof,
      ),
    );
  };

  const startDismissProof = (proof) => {
    if (!canEdit) return;
    setProofAction({ type: "dismiss", proofId: proof.id });
    setProofActionNote("");
    setProofLinkTxnId("");
  };

  const startLinkProof = async (proof) => {
    if (!canEdit || !selectedRegistration?.id) return;
    setProofAction({ type: "link", proofId: proof.id });
    setProofActionNote("");
    setProofLinkTxnId("");
    setProofLinkOptions([]);
    try {
      const response = await feeTransactionsApi.getByRegistration(
        selectedRegistration.id,
      );
      setProofLinkOptions(response.data || []);
    } catch (error) {
      console.error("Error loading fee transactions:", error);
      notificationCtx.show({
        message: "Failed to load recorded payments for linking.",
        stat: "error",
      });
      resetProofAction();
    }
  };

  const submitProofAction = async () => {
    if (!proofAction?.proofId || proofActionBusy) return;
    setProofActionBusy(true);
    try {
      if (proofAction.type === "dismiss") {
        const response = await feeTransactionsApi.dismissProof(
          proofAction.proofId,
          { adminNote: proofActionNote.trim() || undefined },
        );
        applyProofUpdate(response.data?.proof);
        notificationCtx.show({
          message: "Payment proof dismissed.",
          stat: "success",
        });
      } else if (proofAction.type === "link") {
        if (!proofLinkTxnId) {
          notificationCtx.show({
            message: "Select an existing fee transaction to link.",
            stat: "error",
          });
          setProofActionBusy(false);
          return;
        }
        const response = await feeTransactionsApi.linkProof(
          proofAction.proofId,
          {
            feeTransactionId: Number(proofLinkTxnId),
            adminNote: proofActionNote.trim() || undefined,
          },
        );
        applyProofUpdate(response.data?.proof);
        notificationCtx.show({
          message: "Payment proof linked to existing transaction.",
          stat: "success",
        });
      }
      resetProofAction();
      await fetchRegistrations();
    } catch (error) {
      console.error("Error updating payment proof:", error);
      notificationCtx.show({
        message:
          error.response?.data?.message ||
          "Failed to update payment proof.",
        stat: "error",
      });
      setProofActionBusy(false);
    }
  };

  const viewTransactions = async (registrationId) => {
    try {
      const response = await feeTransactionsApi.getByRegistration(
        registrationId,
      );
      setTransactionData(response.data);
      setShowTransactionModal(true);
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      notificationCtx.show({
        message: "Failed to fetch transaction details.",
        stat: "error",
      });
    }
  };

  const [usercourses, setusercourses] = useState([]);
  const [showusermodal, setshowusermodal] = useState(false);

  const showUserDetail = async (registration) => {
    try {
      const response = await registrationsApi.myCourses({
        user: registration.email,
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      if (!rows.length) {
        notificationCtx.show({
          message: "No registration found for this student.",
          stat: "error",
        });
        return;
      }
      setusercourses(
        rows.map((row) => ({
          ...row,
          parentsPhoneNumber:
            row.parentsPhoneNumber || registration.parentsPhoneNumber || "",
        })),
      );
      setshowusermodal(true);
    } catch (error) {
      console.error(
        "Error:",
        error.response ? error.response.data : error.message,
      );
      notificationCtx.show({
        message: "Failed to load student details.",
        stat: "error",
      });
    }
  };

  const [editingCell, setEditingCell] = useState({
    id: null,
    field: null,
  });

  const startEditing = (id, field) => {
    setEditingCell({ id, field });
  };

  const stopEditing = () => {
    setEditingCell({ id: null, field: null });
  };

  const isEditing = (id, field) =>
    editingCell.id === id && editingCell.field === field;

  if (authLoading || permLoading || initialLoading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }
  return (
    <div className="mb-3">
      <Navbar />
      {isAuthenticated && hasPermission && (
        <div
          className="d-flex flex-column"
          style={{
            minHeight: "0",
            height: "113vh",
            overflow: "hidden",
            padding: "10px 10px 0",
          }}
        >
          {/* FIXED HEADER */}
          <div
            style={{
              margin: "10px 50px 0",
              padding: "24px 28px",
              backgroundColor: "#d5d1fd",
              borderRadius: "20px",
              flexShrink: 0,
              zIndex: 10,
              boxShadow: "0 2px 8px rgba(48,45,69,0.04)",
            }}
          >
            {/* ================= HEADER ================= */}
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                marginBottom: "20px",
                gap: "30px",
              }}
            >
              {/* Title */}
              <div>
                <h1
                  className="mb-1"
                  style={{
                    // fontSize: "28px",
                    fontWeight: "700",
                    color: "#302d45",
                  }}
                >
                  ALL REGISTRATIONS
                </h1>

                <small
                  style={{
                    color: "#625d75",
                    fontSize: "13px",
                  }}
                >
                  Manage registrations, payments and reports
                </small>
              </div>

              {/* ================= UPLOAD ACTIONS ================= */}
              <BulkCsvControls
                canEdit={canEdit}
                sampleRows={REGISTRATION_UPLOAD_SAMPLE}
                templateFilename="registrations_upload_template.csv"
                endpoint={REGISTRATION_BULK_UPLOAD_ENDPOINT}
                notificationCtx={notificationCtx}
                onSuccess={fetchRegistrations}
              />
            </div>

            {/* ================= FILTERS ================= */}
            <div
              style={{
                backgroundColor: "rgba(255,255,255,0.45)",
                borderRadius: "12px",
                padding: "16px",
              }}
            >
              {/* Filter heading */}
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  color: "#403b55",
                  marginBottom: "10px",
                  letterSpacing: "0.3px",
                }}
              >
                FILTERS
              </div>

              {/* ================= MAIN FILTERS ================= */}
              <div className="row g-3">
                {/* ================= FILTER BY NAME ================= */}
                <div className="col-md-2">
                  <input
                    type="text"
                    placeholder="Filter by Name"
                    value={filters.name}
                    onChange={(e) =>
                      handleFilterChange("name", e.target.value)
                    }
                    className="form-control"
                    style={{
                      height: "40px",
                      border: "none",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      fontSize: "13px",
                      boxShadow: "0 2px 5px rgba(48,45,69,0.03)",
                    }}
                  />
                </div>

                {/* ================= FILTER BY EMAIL ================= */}
                <div className="col-md-2">
                  <input
                    type="text"
                    placeholder="Filter by Email"
                    value={filters.email}
                    onChange={(e) =>
                      handleFilterChange("email", e.target.value)
                    }
                    className="form-control"
                    style={{
                      height: "40px",
                      border: "none",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      fontSize: "13px",
                      boxShadow: "0 2px 5px rgba(48,45,69,0.03)",
                    }}
                  />
                </div>

                {/* ================= FILTER BY COURSE ================= */}
                <div className="col-md-2">
                  <select
                    value={filters.courseId}
                    onChange={(e) =>
                      handleFilterChange("courseId", e.target.value)
                    }
                    className="form-select"
                    style={{
                      height: "40px",
                      border: "none",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      fontSize: "13px",
                      boxShadow: "0 2px 5px rgba(48,45,69,0.03)",
                    }}
                  >
                    <option value="">Filter by Course</option>

                    {allcourses.map((course) => (
                      <option value={course.id} key={course.id}>
                        {course.title} - {course.Teacher?.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ================= FILTER BY FEE MODE ================= */}
                <div className="col-md-2">
                  <select
                    value={filters.feeMode}
                    onChange={(e) =>
                      handleFilterChange("feeMode", e.target.value)
                    }
                    className="form-select"
                    style={{
                      height: "40px",
                      border: "none",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      fontSize: "13px",
                      boxShadow: "0 2px 5px rgba(48,45,69,0.03)",
                    }}
                  >
                    <option value="">Filter by Fee Mode</option>
                    <option value="full">Full</option>
                    <option value="partial">Partial</option>
                  </select>
                </div>

                {/* ================= FILTER BY PAYMENT STATUS ================= */}
                <div className="col-md-2">
                  <select
                    value={filters.paymentStatus}
                    onChange={(e) =>
                      handleFilterChange("paymentStatus", e.target.value)
                    }
                    className="form-select"
                    style={{
                      height: "40px",
                      border: "none",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      fontSize: "13px",
                      boxShadow: "0 2px 5px rgba(48,45,69,0.03)",
                    }}
                  >
                    <option value="">Filter by Payment Status</option>
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="partially_paid">Partially Paid</option>
                    <option value="pending">Pending enrollment</option>
                  </select>
                </div>

                {/* ================= FILTER BY BALANCE ================= */}
                <div className="col-md-2">
                  <select
                    value={filters.balance}
                    onChange={(e) =>
                      handleFilterChange("balance", e.target.value)
                    }
                    className="form-select"
                    style={{
                      height: "40px",
                      border: "none",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      fontSize: "13px",
                      boxShadow: "0 2px 5px rgba(48,45,69,0.03)",
                    }}
                  >
                    <option value="">Filter by Balance</option>
                    <option value="positive">Positive</option>
                    <option value="zero">Zero</option>
                    <option value="negative">Negative</option>
                  </select>
                </div>
              </div>

              {/* ================= DATE + ACTIONS ================= */}
              <div
                className="d-flex align-items-end justify-content-between"
                style={{
                  marginTop: "16px",
                  paddingTop: "15px",
                  borderTop: "1px solid rgba(80,70,110,0.10)",
                }}
              >
                {/* Date Filter */}
                <div>
                  <label
                    className="form-label mb-1"
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      color: "#514b65",
                      letterSpacing: "0.2px",
                    }}
                  >
                    PAYMENT DATE RANGE
                  </label>

                  <div className="d-flex align-items-center gap-2">
                    <input
                      className="form-control"
                      type="date"
                      value={filterstartDate}
                      onChange={(e) => setFilterStartDate(e.target.value)}
                      style={{
                        width: "165px",
                        height: "38px",
                        border: "none",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        fontSize: "12px",
                      }}
                    />

                    <span
                      style={{
                        color: "#77718b",
                        fontSize: "12px",
                      }}
                    >
                      to
                    </span>

                    <input
                      className="form-control"
                      type="date"
                      value={filterendDate}
                      onChange={(e) => setFilterEndDate(e.target.value)}
                      style={{
                        width: "165px",
                        height: "38px",
                        border: "none",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        fontSize: "12px",
                      }}
                    />

                    <button
                      className="btn"
                      disabled={!filterstartDate || !filterendDate}
                      onClick={() => {
                        const startDate = new Date(filterstartDate);
                        const endDate = new Date(filterendDate);

                        if (startDate > endDate) {
                          notificationCtx.show({
                            message: "Start date cannot be after end date.",
                          });
                          return;
                        }

                        setAppliedDateRange({
                          fromDate: filterstartDate,
                          toDate: filterendDate,
                        });
                        setCurrentPage(1);
                      }}
                      style={{
                        height: "38px",
                        backgroundColor: "#fff",
                        color: "#514637",
                        border: "none",
                        fontWeight: "600",
                        fontSize: "12px",
                        padding: "0 15px",
                        borderRadius: "8px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Apply Date Filter
                    </button>
                  </div>
                </div>

                {/* ================= ACTIONS ================= */}
                <div
                  className="d-flex align-items-center"
                  style={{
                    gap: "8px",
                  }}
                >
                  <button
                    className="btn"
                    onClick={togglePendingVerification}
                    style={{
                      height: "38px",
                      backgroundColor: filters.requiresVerification
                        ? "#bff1db"
                        : "#ffffff",
                      border: filters.requiresVerification
                        ? "1px solid #efd19c"
                        : "1px solid #e5e1ef",
                      color: filters.requiresVerification
                        ? "#bff1db"
                        : "#5f5a68",
                      fontWeight: "600",
                      fontSize: "13px",
                      padding: "0 14px",
                      borderRadius: "9px",
                      whiteSpace: "nowrap",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      boxShadow: filters.requiresVerification
                        ? "0 2px 6px rgba(180, 140, 70, 0.10)"
                        : "0 1px 3px rgba(40, 35, 60, 0.04)",
                      transition: "all 0.18s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow =
                        "0 3px 8px rgba(40, 35, 60, 0.10)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        filters.requiresVerification
                          ? "0 2px 6px rgba(180, 140, 70, 0.10)"
                          : "0 1px 3px rgba(40, 35, 60, 0.04)";
                    }}
                  >
                    <span
                      style={{
                        width: "7px",
                        height: "7px",
                        borderRadius: "50%",
                        backgroundColor: filters.requiresVerification
                          ? "#fff"
                          : "#aaa5b3",
                        flexShrink: 0,
                      }}
                    />

                    {filters.requiresVerification
                      ? "Showing Pending Verification"
                      : "Show Pending Verification"}
                  </button>

                  <button
                    className="btn"
                    onClick={downloadPDF}
                    style={{
                      height: "38px",
                      backgroundColor: "#f2c4c4",
                      border: "none",
                      color: "#654343",
                      fontWeight: "600",
                      fontSize: "13px",
                      padding: "0 15px",
                      borderRadius: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Download PDF
                  </button>

                  <button
                    className="btn"
                    onClick={downloadCsvExport}
                    style={{
                      height: "38px",
                      backgroundColor: "#f9e1be",
                      border: "none",
                      color: "#62503a",
                      fontWeight: "600",
                      fontSize: "13px",
                      padding: "0 15px",
                      borderRadius: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Download CSV
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              margin: "10px 50px",
              borderRadius: "20px",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#fff",
            }}
          >
            {data ? (
              <>
                {/* ================= TABLE AREA ================= */}
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                    borderRadius: "16px",
                    backgroundColor: "#fff",
                    boxShadow: "0 3px 12px rgba(48, 45, 69, 0.08)",
                    border: "1px solid #eeeaf8",
                  }}
                >
                  {/* Horizontal scroll container */}
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      overflowX: "auto",
                      overflowY: "auto",
                    }}
                  >
                    <table
                      border="0"
                      cellPadding="10"
                      cellSpacing="0"
                      style={{
                        minWidth: "1100px",
                        width: "100%",
                        margin: "0",
                        tableLayout: "auto",
                        background: "#ffffff",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                      }}
                    >
                      <colgroup>
                        {columnKeys.map((key) => (
                          <col
                            key={key}
                            style={{
                              width: `${columnWidths[key] || 100}px`,
                            }}
                          />
                        ))}
                      </colgroup>
                      {/* ================= TABLE HEADER ================= */}
                      <thead>
                        <tr>
                          {columnKeys.map((key, index, arr) => (
                            <th
                              key={key}
                              style={{
                                position: "sticky",
                                top: 0,
                                zIndex: 5,

                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",

                                backgroundColor: "#d5d1fd",
                                color: "#302d45",

                                fontWeight: "700",
                                padding: "14px 16px",

                                textAlign: "center",
                                letterSpacing: "0.3px",
                                fontSize: "13px",

                                borderBottom: "1px solid #c8c2ed",

                                boxShadow: "0 2px 4px rgba(48, 45, 69, 0.06)",
                              }}
                            >
                              {columnHeaderLabel(key)}
                              {key === "fee" || key === "courses" ? (
                                <p style={{ fontSize: "0.6rem", color: "red" }}>
                                  *registered courses only
                                </p>
                              ) : (
                                <p
                                  style={{
                                    fontSize: "0.6rem",
                                    color: "#d5d1fd",
                                  }}
                                >
                                  -
                                </p>
                              )}
                              {index < arr.length - 1 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    right: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "5px",
                                    cursor: "col-resize",
                                    background: "transparent",
                                  }}
                                  onMouseDown={(e) => handleResize(e, key)}
                                />
                              )}
                            </th>
                          ))}
                        </tr>
                        {/* <tr>
                        {columnKeys.map((key) => (
                          <th key={key}>
                            {key.startsWith("feePaymentProof") ||
                            key.startsWith("transactions") ? null : key ===
                                "fee" || key === "courses" ? (
                                <p style={{ fontSize: "0.7rem", color: "red" }}>
                                  *registered courses only
                                </p>
                            ) : (
                              <input
                                type="text"
                                placeholder={`Filter ${key}`}
                                value={columnFilters[key] || ""}
                                onChange={(e) =>
                                  handleColumnFilterChange(key, e.target.value)
                                }
                                style={{ width: "100%" }}
                              />
                            )}
                          </th>
                        ))}
                      </tr> */}
                      </thead>

                      {/* ================= TABLE BODY ================= */}
                      <tbody>
                        {data.map((registration, index) => {
                          const needsAction = (registration.feePaymentProofs || []).some(
                            (proof) =>
                              proof.status === "pending" ||
                              (!proof.status && !proof.feeTransactionId),
                          );
                          const listRailColor = registration.blackListed
                            ? "#1f1b2e"
                            : registration.greyListed
                              ? "#8a8f9c"
                              : null;
                          // Outer (leftmost) = list status; next = pending verification.
                          const railCount =
                            (listRailColor ? 1 : 0) + (needsAction ? 1 : 0);
                          const railReservePx = Math.max(railCount, 1) * 6;

                          const td_style = {
                            whiteSpace: "normal",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            backgroundColor: "#fff",
                            borderBottom: "1px solid #f0edf7",
                            fontWeight: 400,
                            padding: "13px 16px",
                            fontSize: "13px",
                            color: "#2c2838",
                          };
                          return (
                            <tr key={index}>
                              <td
                                style={{
                                  ...td_style,
                                  position: "relative",
                                  padding: "14px 16px",
                                  textAlign: "left",
                                  fontSize: "13px",
                                  color: "#403b55",
                                  borderBottom: "1px solid #f0eef4",
                                  borderLeft: `${railReservePx}px solid transparent`,
                                  lineHeight: "1.7",
                                }}
                              >
                                {railCount > 0 && (
                                  <span
                                    aria-hidden="true"
                                    style={{
                                      position: "absolute",
                                      left: 0,
                                      top: 0,
                                      bottom: 0,
                                      display: "flex",
                                      width: railCount * 6,
                                    }}
                                  >
                                    {listRailColor ? (
                                      <span
                                        style={{
                                          flex: "0 0 6px",
                                          backgroundColor: listRailColor,
                                        }}
                                      />
                                    ) : null}
                                    {needsAction ? (
                                      <span
                                        style={{
                                          flex: "0 0 6px",
                                          backgroundColor: "#d4a15c",
                                        }}
                                      />
                                    ) : null}
                                  </span>
                                )}
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "stretch",
                                    gap: "4px",
                                    width: "100%",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: 600,
                                      color: "#2c2838",
                                      textAlign: "left",
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {registration.name}
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "flex-start",
                                      gap: "2px",
                                      fontSize: "11px",
                                      color: "#8a8498",
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    <div>{registration.email}</div>
                                    {registration.phone ? (
                                      <div>{registration.phone}</div>
                                    ) : null}
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: "8px",
                                      marginTop: 0,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#8a8498",
                                        textAlign: "left",
                                        lineHeight: 1.4,
                                      }}
                                    >
                                      Reg ID: {registration.id}
                                    </div>
                                    <AppTooltip label="View student details">
                                      <span
                                        onClick={() =>
                                          showUserDetail(registration)
                                        }
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          width: "28px",
                                          height: "28px",
                                          flexShrink: 0,
                                          borderRadius: "7px",
                                          backgroundColor: "#d5d1fd",
                                          color: "#403b55",
                                          textDecoration: "none",
                                          transition: "all 0.2s ease",
                                          cursor: "pointer",
                                        }}
                                      >
                                        <i
                                          className="fa-solid fa-arrow-up-right-from-square"
                                          style={{
                                            fontSize: "11px",
                                          }}
                                        />
                                      </span>
                                    </AppTooltip>
                                  </div>
                                </div>
                              </td>
                              <td style={td_style}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "8px",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      color: "#625d75",
                                      lineHeight: "1.5",
                                    }}
                                  >
                                    {registration.courses
                                      .filter(
                                        (course) =>
                                          course.status === "enrolled" ||
                                          course.status === "pending",
                                      )
                                      .map((course) => course.title)
                                      .join(", ")}
                                  </span>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setAllCoursesModalData(
                                        registration.courses,
                                      );
                                      setSelectedRegistration(registration);
                                      setShowAllCoursesModal(true);
                                    }}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "7px",
                                      padding: "6px 10px",
                                      border: "1px solid #ddd9f7",
                                      borderRadius: "8px",
                                      backgroundColor: "#faf9fe",
                                      color: "#625d75",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <i
                                      className="fa-solid fa-layer-group"
                                      style={{
                                        fontSize: "11px",
                                        color: "#77718e",
                                      }}
                                    />
                                    View All
                                  </button>
                                </div>
                              </td>
                              <td
                                style={{
                                  ...td_style,
                                  width: "130px",
                                  minWidth: "130px",
                                  maxWidth: "130px",
                                }}
                              >
                                {editingFeeMode === registration.id ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "5px",
                                      width: "100%",
                                    }}
                                  >
                                    <select
                                      value={registration.feemode || ""}
                                      onChange={(e) =>
                                        handleEdit(
                                          registration.id,
                                          "feemode",
                                          e.target.value,
                                        )
                                      }
                                      className="form-select"
                                      style={{
                                        width: "88px",
                                        minWidth: "0",
                                        fontSize: "12px",
                                        border: "1px solid #e5e1ef",
                                        borderRadius: "7px",
                                      }}
                                    >
                                      <option value="full">Full</option>
                                      <option value="partial">Partial</option>
                                    </select>

                                    <AppTooltip label="Cancel">
                                    <button
                                      type="button"
                                      className="btn"
                                      onClick={() => setEditingFeeMode(null)}
                                      style={{
                                        flexShrink: 0,
                                        border: "none",
                                        backgroundColor: "#f1eff5",
                                        color: "#625d75",
                                        width: "28px",
                                        height: "28px",
                                        padding: 0,
                                        borderRadius: "6px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <i className="fa-solid fa-xmark" />
                                    </button>
                                    </AppTooltip>
                                  </div>
                                ) : (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "7px",
                                      width: "100%",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: "#625d75",
                                        textTransform: "capitalize",
                                      }}
                                    >
                                      {registration.feemode || "Not Set"}
                                    </span>

                                    {canEdit && (
                                    <AppTooltip label="Edit Fee Mode">
                                    <button
                                      type="button"
                                      className="btn"
                                      onClick={() =>
                                        setEditingFeeMode(registration.id)
                                      }
                                      style={{
                                        flexShrink: 0,
                                        border: "none",
                                        backgroundColor: "transparent",
                                        color: "#625d75",
                                        padding: "3px 5px",
                                        fontSize: "12px",
                                      }}
                                    >
                                      <i className="fa-solid fa-pen" />
                                    </button>
                                    </AppTooltip>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td style={td_style}>
                                <span
                                  style={{
                                    display: "inline-block",
                                    padding: "5px 9px",
                                    fontSize: "11px",
                                    fontWeight: "700",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  PKR{" "}
                                  {registration.courses
                                    .reduce((acc, curr) => {
                                      if (
                                        curr.status === "enrolled" ||
                                        curr.status === "pending"
                                      ) {
                                        const fee = parseCourseFee(curr.fee);
                                        const courseDiscount =
                                          parseCourseDiscount(curr.discount);

                                        return (
                                          acc +
                                          Math.max(
                                            0,
                                            Math.round(fee - courseDiscount),
                                          )
                                        );
                                      }

                                      return acc;
                                    }, 0)
                                    .toLocaleString()}
                                  <span
                                    style={{
                                      fontSize: "10px",
                                      fontWeight: "600",
                                      marginLeft: "3px",
                                      opacity: 0.75,
                                    }}
                                  >
                                    / month
                                  </span>
                                </span>
                              </td>
                              <td className="text-center" style={{ td_style }}>
                                {(() => {
                                  const statusLabel =
                                    formatRegistrationPaymentStatus(
                                      registration,
                                    );
                                  const isPending =
                                    statusLabel === "Pending enrollment";
                                  const isPaid = statusLabel === "paid";
                                  const isPartial =
                                    statusLabel === "Partially Paid";
                                  const isUnpaid = statusLabel === "unpaid";
                                  return (
                                    <span
                                      style={{
                                        display: "inline-block",
                                        padding: "5px 9px",
                                        borderRadius: "7px",
                                        backgroundColor: isPaid
                                          ? "#bff1db"
                                          : isPartial || isPending
                                            ? "#f9e1be"
                                            : isUnpaid
                                              ? "#f2c4c4"
                                              : "#f3f1f7",
                                        color: isPaid
                                          ? "#355c4a"
                                          : isPartial || isPending
                                            ? "#62503a"
                                            : isUnpaid
                                              ? "#6b3f3f"
                                              : "#625d75",
                                        fontSize: "11px",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {statusLabel}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td style={td_style}>
                                {(() => {
                                  const owed = Number(registration.owedTotal) || 0;
                                  const credit =
                                    Number(registration.creditTotal) || 0;
                                  const signed =
                                    owed > 0
                                      ? owed
                                      : credit > 0
                                        ? -credit
                                        : parseInt(
                                            String(
                                              registration.balance ?? "0",
                                            ).replace(/[^0-9-]/g, ""),
                                            10,
                                          ) || 0;
                                  return (
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "3px",
                                        alignItems: "flex-start",
                                      }}
                                    >
                                      <span
                                        style={{
                                          display: "inline-block",
                                          padding: "5px 9px",
                                          borderRadius: "7px",
                                          backgroundColor:
                                            signed > 0
                                              ? "#f9e1be"
                                              : signed < 0
                                                ? "#d5d1fd"
                                                : "#f3f1f7",
                                          color:
                                            signed > 0
                                              ? "#62503a"
                                              : signed < 0
                                                ? "#403b55"
                                                : "#625d75",
                                          fontSize: "11px",
                                          fontWeight: "700",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {registration.balance || "PKR 0"}
                                      </span>
                                      {credit > 0 ? (
                                        <span
                                          style={{
                                            fontSize: "10px",
                                            color: "#625d75",
                                            fontWeight: 600,
                                            paddingLeft: "2px",
                                          }}
                                        >
                                          {owed > 0
                                            ? `Owed ${owed.toLocaleString()} · Credit ${credit.toLocaleString()}`
                                            : `Credit PKR ${credit.toLocaleString()}`}
                                        </span>
                                      ) : null}
                                    </div>
                                  );
                                })()}
                              </td>
                              <td style={td_style}>
                                {(registration.feePaymentProofs || []).length >
                                0 ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      resetProofAction();
                                      setFeeImagesModalData(
                                        registration.feePaymentProofs || [],
                                      );
                                      setSelectedRegistration(registration);
                                      setShowFeeImagesModal(true);
                                    }}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "7px",
                                      padding: "6px 10px",
                                      border: "1px solid #ddd9f7",
                                      borderRadius: "8px",
                                      backgroundColor: "#faf9fe",
                                      color: "#625d75",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <i
                                      className="fa-regular fa-images"
                                      style={{
                                        fontSize: "12px",
                                        color: "#77718e",
                                      }}
                                    />

                                    <span>View Images</span>

                                    <span
                                      style={{
                                        padding: "2px 6px",
                                        borderRadius: "6px",
                                        backgroundColor: "#d5d1fd",
                                        color: "#4f4968",
                                        fontSize: "10px",
                                        fontWeight: "700",
                                      }}
                                    >
                                      {registration.feePaymentProofs.length}
                                    </span>
                                  </button>
                                ) : (
                                  <span
                                    style={{
                                      color: "#aaa6b4",
                                      fontSize: "11px",
                                      fontWeight: "500",
                                    }}
                                  >
                                    No images
                                  </span>
                                )}
                              </td>
                              <td style={td_style}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <button
                                    type="button"
                                    onClick={() =>
                                      viewTransactions(registration.id)
                                    }
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "6px",
                                      padding: "6px 10px",
                                      border: "1px solid #ddd9f7",
                                      borderRadius: "8px",
                                      backgroundColor: "#faf9fe",
                                      color: "#625d75",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <i
                                      className="fa-solid fa-receipt"
                                      style={{
                                        fontSize: "11px",
                                        color: "#77718e",
                                      }}
                                    />
                                    View
                                  </button>

                                  {canEdit && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedRegistration(registration);

                                      setFeeImage("");
                                      setPaymentDate("");
                                      setTransactionId("");
                                      setAmountPaid("");
                                      setSelectedProofId(null);
                                      setSelectedFeeTransactionId(null);

                                      setIsExistingPayment(false);
                                      setShowfeeImageModal(true);
                                    }}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "6px",
                                      padding: "6px 10px",
                                      border: "1px solid #cce9d9",
                                      borderRadius: "8px",
                                      backgroundColor: "#f5fcf8",
                                      color: "#355c4a",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <i
                                      className="fa-solid fa-plus"
                                      style={{
                                        fontSize: "10px",
                                      }}
                                    />
                                    Add New
                                  </button>
                                  )}
                                </div>
                              </td>
                              <td
                                style={{
                                  ...td_style,
                                  width: "150px",
                                  minWidth: "150px",
                                  maxWidth: "150px",
                                }}
                              >
                                {isEditing(registration.id, "duedate") ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "5px",
                                      width: "100%",
                                    }}
                                  >
                                    <input
                                      type="date"
                                      value={
                                        registration.duedate?.split("T")[0] ||
                                        ""
                                      }
                                      onChange={(e) =>
                                        handleInputChange(
                                          registration.id,
                                          "duedate",
                                          e.target.value,
                                        )
                                      }
                                      style={{
                                        width: "105px",
                                        minWidth: "0",
                                        fontSize: "12px",
                                        padding: "5px 6px",
                                        border: "1px solid #e5e1ef",
                                        borderRadius: "7px",
                                        color: "#625d75",
                                      }}
                                    />

                                    <AppTooltip label="Cancel">
                                    <button
                                      type="button"
                                      className="btn"
                                      onClick={() => {
                                        stopEditing();
                                      }}
                                      style={{
                                        flexShrink: 0,
                                        border: "none",
                                        backgroundColor: "#f1eff5",
                                        color: "#625d75",
                                        width: "28px",
                                        height: "28px",
                                        padding: 0,
                                        borderRadius: "6px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <i className="fa-solid fa-xmark" />
                                    </button>
                                    </AppTooltip>

                                    <AppTooltip label="Save">
                                    <button
                                      type="button"
                                      className="btn"
                                      disabled={fieldSaving}
                                      onClick={async () => {
                                        await handleEdit(
                                          registration.id,
                                          "duedate",
                                          registration.duedate,
                                        );
                                        stopEditing();
                                      }}
                                      style={{
                                        flexShrink: 0,
                                        border: "none",
                                        backgroundColor: "#bff1db",
                                        color: "#355c4a",
                                        width: "28px",
                                        height: "28px",
                                        padding: 0,
                                        borderRadius: "6px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <i className="fa-solid fa-check" />
                                    </button>
                                    </AppTooltip>
                                  </div>
                                ) : (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "7px",
                                      width: "100%",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: "#625d75",
                                      }}
                                    >
                                      {formatDisplayDate(
                                        registration.duedate,
                                        "-",
                                      )}
                                    </span>

                                    {canEdit && (
                                    <AppTooltip label="Edit Due Date">
                                    <button
                                      type="button"
                                      className="btn"
                                      onClick={() =>
                                        startEditing(registration.id, "duedate")
                                      }
                                      style={{
                                        flexShrink: 0,
                                        border: "none",
                                        backgroundColor: "transparent",
                                        color: "#625d75",
                                        padding: "3px 5px",
                                        fontSize: "12px",
                                      }}
                                    >
                                      <i className="fa-solid fa-pen" />
                                    </button>
                                    </AppTooltip>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td
                                style={{
                                  ...td_style,
                                  width: "180px",
                                  minWidth: "180px",
                                  maxWidth: "180px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "7px",
                                    width: "100%",
                                  }}
                                >
                                  <AppTooltip
                                    label={
                                      registration.comments || "No comments"
                                    }
                                    wrap
                                  >
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: "600",
                                      color: "#625d75",
                                      maxWidth: "125px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {registration.comments || "-"}
                                  </span>
                                  </AppTooltip>

                                  <AppTooltip label="View Comment">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedComment(registration);
                                      setCommentValue(
                                        registration.comments || "",
                                      );
                                      setEditingComment(false);
                                    }}
                                    style={{
                                      width: "28px",
                                      height: "28px",
                                      padding: 0,
                                      border: "1px solid #e5e1ef",
                                      borderRadius: "7px",
                                      backgroundColor: "#fff",
                                      color: "#625d75",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <i
                                      className="fa-regular fa-comment"
                                      style={{ fontSize: "12px" }}
                                    />
                                  </button>
                                  </AppTooltip>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div style={{ backgroundColor: "#f9f8fd", padding: "0 4px" }}>
                  <PaginationFooter
                    page={currentPage}
                    limit={limit}
                    total={total}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemLabel="registrations"
                    disabled={refreshing}
                  />
                </div>

                {selectedComment && (
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      backgroundColor: "rgba(48, 44, 64, 0.45)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1050,
                      padding: "20px",
                    }}
                    onClick={() => {
                      setSelectedComment(null);
                      setEditingComment(false);
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        maxWidth: "500px",
                        backgroundColor: "#fff",
                        borderRadius: "14px",
                        boxShadow: "0 12px 35px rgba(48, 44, 64, 0.18)",
                        overflow: "hidden",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Header */}
                      <div
                        style={{
                          padding: "16px 20px",
                          backgroundColor: "#d5d1fd",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "9px",
                          }}
                        >
                          <div
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "8px",
                              backgroundColor: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#625d75",
                            }}
                          >
                            <i className="fa-regular fa-comment" />
                          </div>

                          <div>
                            <div
                              style={{
                                fontSize: "14px",
                                fontWeight: "700",
                                color: "#302c40",
                              }}
                            >
                              Comment
                            </div>

                            <div
                              style={{
                                fontSize: "11px",
                                color: "#625d75",
                                marginTop: "2px",
                              }}
                            >
                              {selectedComment.name || "Registration"}
                            </div>
                          </div>
                        </div>

                        <AppTooltip label="Close">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedComment(null);
                            setEditingComment(false);
                          }}
                          style={{
                            width: "30px",
                            height: "30px",
                            padding: 0,
                            border: "none",
                            borderRadius: "7px",
                            backgroundColor: "rgba(255,255,255,0.65)",
                            color: "#625d75",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          <i className="fa-solid fa-xmark" />
                        </button>
                        </AppTooltip>
                      </div>

                      {/* Content */}
                      <div style={{ padding: "20px" }}>
                        {editingComment ? (
                          <textarea
                            value={commentValue}
                            onChange={(e) => setCommentValue(e.target.value)}
                            autoFocus
                            placeholder="Enter comment..."
                            style={{
                              width: "100%",
                              minHeight: "150px",
                              resize: "vertical",
                              padding: "11px 12px",
                              border: "1px solid #dcd8e8",
                              borderRadius: "8px",
                              outline: "none",
                              fontSize: "13px",
                              lineHeight: "1.5",
                              color: "#302c40",
                              backgroundColor: "#fff",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              minHeight: "100px",
                              maxHeight: "260px",
                              overflowY: "auto",
                              padding: "13px 14px",
                              backgroundColor: "#faf9fc",
                              border: "1px solid #e5e1ef",
                              borderRadius: "8px",
                              fontSize: "13px",
                              lineHeight: "1.6",
                              color: "#625d75",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {selectedComment.comments ||
                              "No comment has been added."}
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      <div
                        style={{
                          padding: "0 20px 18px",
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: "8px",
                        }}
                      >
                        {editingComment ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setCommentValue(selectedComment.comments || "");
                                setEditingComment(false);
                              }}
                              style={{
                                height: "34px",
                                padding: "0 13px",
                                border: "1px solid #e5e1ef",
                                borderRadius: "7px",
                                backgroundColor: "#fff",
                                color: "#625d75",
                                fontSize: "12px",
                                fontWeight: "600",
                                cursor: "pointer",
                              }}
                            >
                              Cancel
                            </button>

                            <button
                              type="button"
                              disabled={fieldSaving}
                              onClick={async () => {
                                await handleEdit(
                                  selectedComment.id,
                                  "comments",
                                  commentValue,
                                );

                                setSelectedComment((prev) => ({
                                  ...prev,
                                  comments: commentValue,
                                }));

                                setEditingComment(false);
                              }}
                              style={{
                                height: "34px",
                                padding: "0 14px",
                                border: "1px solid #a8dfc5",
                                borderRadius: "7px",
                                backgroundColor: "#bff1db",
                                color: "#355c4a",
                                fontSize: "12px",
                                fontWeight: "700",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="fa-solid fa-check"
                                style={{ marginRight: "6px" }}
                              />
                              {fieldSaving ? "Saving..." : "Save"}
                            </button>
                          </>
                        ) : (
                          canEdit && (
                          <>
                            <button
                              type="button"
                              onClick={() => setEditingComment(true)}
                              style={{
                                height: "34px",
                                padding: "0 14px",
                                border: "1px solid #e5e1ef",
                                borderRadius: "7px",
                                backgroundColor: "#fff",
                                color: "#625d75",
                                fontSize: "12px",
                                fontWeight: "600",
                                cursor: "pointer",
                              }}
                            >
                              <i
                                className="fa-solid fa-pen"
                                style={{ marginRight: "6px" }}
                              />
                              Edit
                            </button>
                          </>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {showfeeImageModal && (
                  <FeeVerificationModal
                    selectedRegistration={selectedRegistration}
                    setShowfeeImageModal={setShowfeeImageModal}
                    transactionId={transactionId}
                    amountPaid={amountPaid}
                    feeimage={feeimage}
                    paymentDate={paymentDate}
                    setPaymentDate={setPaymentDate}
                    setTransactionId={setTransactionId}
                    setAmountPaid={setAmountPaid}
                    verifyFeePayment={verifyFeePayment}
                    feePaymentProofId={selectedProofId}
                    feeTransactionId={selectedFeeTransactionId}
                    isExistingPayment={isExistingPayment}
                    canEdit={canEdit}
                    onSuccess={async () => {
                      setShowfeeImageModal(false);
                      await fetchRegistrations();
                    }}
                  />
                )}

                {showTransactionModal && (
                  <div
                    className="modal fade show"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(0,0,0,0.7)",
                      zIndex: 10500,
                      padding: "20px",
                    }}
                    tabIndex="-1"
                  >
                    <div
                      className="modal-dialog modal-xl modal-dialog-centered"
                      style={{
                        width: "100%",
                        maxWidth: "1100px",
                        margin: "auto",
                      }}
                    >
                      <div
                        className="modal-content"
                        style={{
                          backgroundColor: "#ffffff",
                          border: "none",
                          borderRadius: "20px",
                          overflow: "hidden",
                          boxShadow: "0 8px 30px rgba(0,0,0,0.18)",
                        }}
                      >
                        {/* Header */}
                        <div
                          className="modal-header"
                          style={{
                            padding: "22px 26px",
                            borderBottom: "1px solid rgba(0,0,0,0.06)",
                            backgroundColor: "#d5d1fd",
                          }}
                        >
                          <div>
                            <h5
                              className="modal-title mb-1"
                              style={{
                                fontSize: "20px",
                                fontWeight: "600",
                                color: "#2f2c35",
                              }}
                            >
                              Transaction Details
                            </h5>

                            {/* <small
                              style={{
                                color: "#6f6a72",
                                fontSize: "13px",
                              }}
                            >
                              View transaction records for this registration
                            </small> */}
                          </div>

                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setShowTransactionModal(false)}
                            aria-label="Close"
                          />
                        </div>

                        {/* Body */}
                        <div
                          className="modal-body"
                          style={{
                            padding: "24px 26px 28px",
                            overflowY: "auto",
                            maxHeight: "75vh",
                          }}
                        >
                          {transactionData.length > 0 ? (
                            <TransactionsViewer data={transactionData} />
                          ) : (
                            <div
                              className="text-center"
                              style={{
                                backgroundColor: "#ffffff",
                                borderRadius: "15px",
                                padding: "60px 20px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "32px",
                                  color: "#625d75",
                                  marginBottom: "15px",
                                }}
                              >
                                <i className="fa-solid fa-receipt" />
                              </div>

                              <h5
                                style={{
                                  marginBottom: "6px",
                                  fontWeight: "600",
                                  color: "#333",
                                }}
                              >
                                No Transactions so far
                              </h5>

                              <p
                                style={{
                                  margin: 0,
                                  color: "#777",
                                  fontSize: "14px",
                                }}
                              >
                                No transaction records are available for this
                                registration.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {showFeeImagesModal && (
                  <div
                    className="modal fade show"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(0, 0, 0, 0.65)",
                      padding: "20px",
                    }}
                    tabIndex="-1"
                  >
                    <div
                      className="modal-dialog modal-lg modal-dialog-centered"
                      style={{
                        width: "100%",
                        maxWidth: "900px",
                        margin: 0,
                      }}
                    >
                      <div
                        className="modal-content"
                        style={{
                          backgroundColor: "#ffffff",
                          border: "none",
                          borderRadius: "20px",
                          overflow: "hidden",
                          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                          maxHeight: "90vh",
                        }}
                      >
                        {/* Header */}
                        <div
                          className="modal-header"
                          style={{
                            backgroundColor: "#d5d1fd",
                            border: "none",
                            padding: "20px 24px",
                            minHeight: "76px",
                          }}
                        >
                          <div>
                            <h5
                              className="modal-title mb-1"
                              style={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#252333",
                              }}
                            >
                              Fee Payment Images
                            </h5>

                            <div
                              style={{
                                fontSize: "13px",
                                color: "#4f4a60",
                                fontWeight: "500",
                              }}
                            >
                              {selectedRegistration?.name || "Student"}
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => {
                              resetProofAction();
                              setShowFeeImagesModal(false);
                            }}
                            aria-label="Close"
                            style={{
                              backgroundColor: "rgba(255,255,255,0.65)",
                              borderRadius: "50%",
                              padding: "10px",
                              opacity: 0.85,
                            }}
                          />
                        </div>

                        {/* Body */}
                        <div
                          className="modal-body"
                          style={{
                            padding: "24px",
                            overflowY: "auto",
                            maxHeight: "calc(90vh - 76px)",
                          }}
                        >
                          {feeImagesModalData &&
                          feeImagesModalData.length > 0 ? (
                            <>
                              {/* Small summary */}
                              <div
                                className="d-flex align-items-center justify-content-between mb-3"
                                style={{
                                  backgroundColor: "#ffffff",
                                  borderRadius: "12px",
                                  padding: "13px 16px",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                }}
                              >
                                <div>
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      color: "#6c6875",
                                    }}
                                  >
                                    Payment records
                                  </span>
                                </div>

                                <span
                                  style={{
                                    backgroundColor: "#f9e1be",
                                    color: "#403a32",
                                    fontWeight: "700",
                                    fontSize: "13px",
                                    padding: "6px 12px",
                                    borderRadius: "20px",
                                  }}
                                >
                                  {feeImagesModalData.length}{" "}
                                  {feeImagesModalData.length === 1
                                    ? "Image"
                                    : "Images"}
                                </span>
                              </div>

                              {proofAction ? (
                                <div
                                  className="mb-3"
                                  style={{
                                    backgroundColor: "#faf9fe",
                                    border: "1px solid #ddd9f7",
                                    borderRadius: "12px",
                                    padding: "14px 16px",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: 700,
                                      color: "#302c40",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    {proofAction.type === "dismiss"
                                      ? "Dismiss this pending proof"
                                      : "Link proof to an existing payment"}
                                  </div>
                                  {proofAction.type === "link" ? (
                                    <label
                                      style={{
                                        display: "block",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        color: "#5a5568",
                                        marginBottom: "6px",
                                      }}
                                    >
                                      Existing fee transaction
                                      <select
                                        className="form-select mt-1"
                                        value={proofLinkTxnId}
                                        onChange={(e) =>
                                          setProofLinkTxnId(e.target.value)
                                        }
                                        disabled={proofActionBusy}
                                      >
                                        <option value="">
                                          Select a recorded payment…
                                        </option>
                                        {proofLinkOptions.map((txn) => (
                                          <option key={txn.id} value={txn.id}>
                                            {txn.transactionId || `Txn #${txn.id}`}
                                            {txn.paymentDate
                                              ? ` · ${formatDisplayDate(txn.paymentDate)}`
                                              : ""}
                                            {txn.amountPaid != null
                                              ? ` · PKR ${txn.amountPaid}`
                                              : ""}
                                          </option>
                                        ))}
                                      </select>
                                    </label>
                                  ) : null}
                                  <label
                                    style={{
                                      display: "block",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      color: "#5a5568",
                                      marginTop:
                                        proofAction.type === "link" ? "10px" : 0,
                                      marginBottom: "6px",
                                    }}
                                  >
                                    Note (optional)
                                    <input
                                      type="text"
                                      className="form-control mt-1"
                                      maxLength={500}
                                      value={proofActionNote}
                                      onChange={(e) =>
                                        setProofActionNote(e.target.value)
                                      }
                                      disabled={proofActionBusy}
                                      placeholder="e.g. duplicate upload"
                                    />
                                  </label>
                                  <div
                                    className="d-flex gap-2 mt-3"
                                    style={{ flexWrap: "wrap" }}
                                  >
                                    <button
                                      type="button"
                                      className="btn"
                                      disabled={proofActionBusy}
                                      onClick={submitProofAction}
                                      style={{
                                        backgroundColor:
                                          proofAction.type === "dismiss"
                                            ? "#f2c4c4"
                                            : "#d5d1fd",
                                        border: "none",
                                        color: "#302c40",
                                        borderRadius: "9px",
                                        padding: "8px 14px",
                                        fontSize: "13px",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {proofActionBusy
                                        ? "Saving…"
                                        : proofAction.type === "dismiss"
                                          ? "Confirm dismiss"
                                          : "Confirm link"}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn"
                                      disabled={proofActionBusy}
                                      onClick={resetProofAction}
                                      style={{
                                        backgroundColor: "#ebe8f2",
                                        border: "none",
                                        color: "#5a5568",
                                        borderRadius: "9px",
                                        padding: "8px 14px",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : null}

                              {/* Table Card */}
                              <div
                                style={{
                                  backgroundColor: "#ffffff",
                                  borderRadius: "16px",
                                  overflow: "hidden",
                                  boxShadow: "0 3px 12px rgba(0,0,0,0.07)",
                                }}
                              >
                                <div style={{ overflowX: "auto" }}>
                                  <table
                                    className="table align-middle mb-0"
                                    style={{
                                      width: "100%",
                                      borderCollapse: "separate",
                                      borderSpacing: 0,
                                    }}
                                  >
                                    <thead>
                                      <tr>
                                        <th
                                          style={{
                                            backgroundColor: "#d5d1fd",
                                            color: "#3f3a45",
                                            fontWeight: "700",
                                            padding: "15px 18px",
                                            textAlign: "center",
                                            fontSize: "13px",
                                            letterSpacing: "0.4px",
                                            borderBottom: "1px solid #e5ded5",
                                            width: "8%",
                                          }}
                                        >
                                          #
                                        </th>

                                        <th
                                          style={{
                                            backgroundColor: "#d5d1fd",
                                            color: "#3f3a45",
                                            fontWeight: "700",
                                            padding: "15px 18px",
                                            textAlign: "center",
                                            fontSize: "13px",
                                            letterSpacing: "0.4px",
                                            borderBottom: "1px solid #e5ded5",
                                            width: "18%",
                                          }}
                                        >
                                          STATUS
                                        </th>

                                        <th
                                          style={{
                                            backgroundColor: "#d5d1fd",
                                            color: "#3f3a45",
                                            fontWeight: "700",
                                            padding: "15px 18px",
                                            textAlign: "center",
                                            fontSize: "13px",
                                            letterSpacing: "0.4px",
                                            borderBottom: "1px solid #e5ded5",
                                            width: "22%",
                                          }}
                                        >
                                          PAYMENT DATE
                                        </th>

                                        <th
                                          style={{
                                            backgroundColor: "#d5d1fd",
                                            color: "#3f3a45",
                                            fontWeight: "700",
                                            padding: "15px 18px",
                                            textAlign: "center",
                                            fontSize: "13px",
                                            letterSpacing: "0.4px",
                                            borderBottom: "1px solid #e5ded5",
                                            width: "52%",
                                          }}
                                        >
                                          ACTIONS
                                        </th>
                                      </tr>
                                    </thead>

                                    <tbody>
                                      {feeImagesModalData
                                        ?.slice()
                                        .reverse()
                                        .map((proof, index) => {
                                          const status =
                                            resolveProofStatus(proof);
                                          const statusColors =
                                            proofStatusStyle(status);
                                          const linkedOrPrimaryTxnId =
                                            proof.feeTransactionId ||
                                            proof.linkedFeeTransactionId ||
                                            null;
                                          const isPending =
                                            status === "pending";
                                          return (
                                            <tr
                                              key={proof.id}
                                              style={{
                                                transition:
                                                  "background-color 0.15s ease",
                                              }}
                                            >
                                              {/* Number */}
                                              <td
                                                className="text-center"
                                                style={{
                                                  padding: "16px 18px",
                                                  color: "#6c6875",
                                                  fontSize: "13px",
                                                  fontWeight: "600",
                                                  borderBottom:
                                                    "1px solid #eeeae5",
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: "30px",
                                                    height: "30px",
                                                    borderRadius: "50%",
                                                    backgroundColor: "#d5d1fd",
                                                    color: "#403b52",
                                                    fontSize: "12px",
                                                    fontWeight: "700",
                                                  }}
                                                >
                                                  {index + 1}
                                                </span>
                                              </td>

                                              {/* Status */}
                                              <td
                                                className="text-center"
                                                style={{
                                                  padding: "16px 18px",
                                                  borderBottom:
                                                    "1px solid #eeeae5",
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    display: "inline-block",
                                                    padding: "4px 10px",
                                                    borderRadius: "999px",
                                                    fontSize: "11px",
                                                    fontWeight: 700,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.04em",
                                                    ...statusColors,
                                                  }}
                                                >
                                                  {status}
                                                </span>
                                                {proof.adminNote ? (
                                                  <div
                                                    style={{
                                                      marginTop: "4px",
                                                      fontSize: "11px",
                                                      color: "#77727d",
                                                      fontStyle: "italic",
                                                    }}
                                                  >
                                                    {proof.adminNote}
                                                  </div>
                                                ) : null}
                                              </td>

                                              {/* Date */}
                                              <td
                                                className="text-center"
                                                style={{
                                                  padding: "16px 18px",
                                                  color: "#34313a",
                                                  fontSize: "14px",
                                                  fontWeight: "500",
                                                  borderBottom:
                                                    "1px solid #eeeae5",
                                                }}
                                              >
                                                {formatDisplayDate(
                                                  proof.paymentDate,
                                                )}
                                              </td>

                                              {/* Actions */}
                                              <td
                                                className="text-center"
                                                style={{
                                                  padding: "16px 18px",
                                                  borderBottom:
                                                    "1px solid #eeeae5",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: "inline-flex",
                                                    flexWrap: "wrap",
                                                    gap: "8px",
                                                    justifyContent: "center",
                                                  }}
                                                >
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      setFeeImage(
                                                        isSafeFeeProofDisplayUrl(
                                                          proof.imageUrl,
                                                        )
                                                          ? proof.imageUrl
                                                          : "",
                                                      );
                                                      setPaymentDate(
                                                        proof.paymentDate || "",
                                                      );
                                                      setSelectedProofId(
                                                        proof.id,
                                                      );
                                                      setSelectedFeeTransactionId(
                                                        linkedOrPrimaryTxnId,
                                                      );
                                                      setTransactionId("");
                                                      setAmountPaid("");
                                                      setIsExistingPayment(
                                                        status === "verified" ||
                                                          status === "linked" ||
                                                          !!proof.feeTransactionId,
                                                      );
                                                      setShowfeeImageModal(
                                                        true,
                                                      );
                                                    }}
                                                    className="btn"
                                                    style={{
                                                      backgroundColor:
                                                        "#d5d1fd",
                                                      border: "none",
                                                      color: "#302c40",
                                                      borderRadius: "9px",
                                                      padding: "8px 16px",
                                                      fontSize: "13px",
                                                      fontWeight: "600",
                                                    }}
                                                  >
                                                    <i
                                                      className="fa-solid fa-image"
                                                      style={{
                                                        marginRight: "7px",
                                                        fontSize: "12px",
                                                      }}
                                                    />
                                                    {isSafeFeeProofDisplayUrl(
                                                      proof.imageUrl,
                                                    )
                                                      ? "View Image"
                                                      : "View Proof"}
                                                  </button>
                                                  {canEdit && isPending ? (
                                                    <>
                                                      <button
                                                        type="button"
                                                        className="btn"
                                                        disabled={
                                                          proofActionBusy
                                                        }
                                                        onClick={() =>
                                                          startLinkProof(proof)
                                                        }
                                                        style={{
                                                          backgroundColor:
                                                            "#f7f6fa",
                                                          border:
                                                            "1px solid #ddd9f7",
                                                          color: "#302c40",
                                                          borderRadius: "9px",
                                                          padding: "8px 12px",
                                                          fontSize: "12px",
                                                          fontWeight: "600",
                                                        }}
                                                      >
                                                        Link
                                                      </button>
                                                      <button
                                                        type="button"
                                                        className="btn"
                                                        disabled={
                                                          proofActionBusy
                                                        }
                                                        onClick={() =>
                                                          startDismissProof(
                                                            proof,
                                                          )
                                                        }
                                                        style={{
                                                          backgroundColor:
                                                            "#faf0f0",
                                                          border:
                                                            "1px solid #f2c4c4",
                                                          color: "#5a3030",
                                                          borderRadius: "9px",
                                                          padding: "8px 12px",
                                                          fontSize: "12px",
                                                          fontWeight: "600",
                                                        }}
                                                      >
                                                        Dismiss
                                                      </button>
                                                    </>
                                                  ) : null}
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </>
                          ) : (
                            /* Empty State */
                            <div
                              style={{
                                backgroundColor: "#ffffff",
                                borderRadius: "16px",
                                padding: "55px 30px",
                                textAlign: "center",
                                boxShadow: "0 3px 12px rgba(0,0,0,0.06)",
                              }}
                            >
                              <div
                                style={{
                                  width: "64px",
                                  height: "64px",
                                  margin: "0 auto 18px",
                                  borderRadius: "50%",
                                  backgroundColor: "#d5d1fd",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <i
                                  className="fa-solid fa-image"
                                  style={{
                                    fontSize: "24px",
                                    color: "#4b465d",
                                  }}
                                />
                              </div>

                              <h5
                                style={{
                                  fontWeight: "700",
                                  color: "#34313a",
                                  marginBottom: "8px",
                                }}
                              >
                                No Fee Images
                              </h5>

                              <p
                                style={{
                                  color: "#77727d",
                                  fontSize: "14px",
                                  marginBottom: 0,
                                }}
                              >
                                No payment images have been uploaded for this
                                registration yet.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {showusermodal && (
                  <UserCoursesModal
                    usercourses={usercourses}
                    onClose={() => setshowusermodal(false)}
                    canEdit={canEdit}
                    onRefresh={async () => {
                      await fetchRegistrations();
                    }}
                  />
                )}

                {showAllCoursesModal && (
                  <div
                    className="modal show"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(0, 0, 0, 0.65)",
                      zIndex: 10500,
                      padding: "24px",
                    }}
                    tabIndex="-1"
                  >
                    <div
                      className="modal-dialog modal-lg modal-dialog-centered"
                      style={{
                        width: "100%",
                        maxWidth: "900px",
                        margin: 0,
                      }}
                    >
                      <div
                        className="modal-content"
                        style={{
                          backgroundColor: "#ffffff",
                          border: "none",
                          borderRadius: "20px",
                          overflow: "hidden",
                          boxShadow: "0 15px 45px rgba(0,0,0,0.18)",
                        }}
                      >
                        {/* ================= HEADER ================= */}
                        <div
                          className="modal-header"
                          style={{
                            borderBottom: "1px solid #e4ddd5",
                            padding: "22px 28px",
                            backgroundColor: "#d5d1fd",
                          }}
                        >
                          <div>
                            <h4
                              className="modal-title mb-1"
                              style={{
                                fontWeight: "700",
                                color: "#2f2f2f",
                                fontSize: "22px",
                                letterSpacing: "0.2px",
                              }}
                            >
                              REGISTERED COURSES
                            </h4>

                            {selectedRegistration?.name && (
                              <div
                                style={{
                                  color: "#6f6a65",
                                  fontSize: "13px",
                                  marginTop: "4px",
                                }}
                              >
                                Courses registered by{" "}
                                <strong style={{ color: "#4f4a5a" }}>
                                  {selectedRegistration.name}
                                </strong>
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setShowAllCoursesModal(false)}
                            aria-label="Close"
                          />
                        </div>

                        {/* ================= BODY ================= */}
                        <div
                          className="modal-body"
                          style={{
                            padding: "24px 28px 28px",
                            maxHeight: "70vh",
                            overflowY: "auto",
                          }}
                        >
                          {allCoursesModalData &&
                          allCoursesModalData.length > 0 ? (
                            <>
                              {/* ================= SUMMARY ================= */}
                              <div
                                className="d-flex justify-content-between align-items-center mb-3"
                                style={{
                                  padding: "13px 16px",
                                  backgroundColor: "#ffffff",
                                  borderRadius: "12px",
                                  border: "1px solid #ebe4dc",
                                  boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                                }}
                              >
                                <div className="d-flex align-items-center">
                                  <div
                                    style={{
                                      width: "34px",
                                      height: "34px",
                                      borderRadius: "9px",
                                      backgroundColor: "#d5d1fd",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      marginRight: "10px",
                                    }}
                                  >
                                    <i
                                      className="fa-solid fa-book-open"
                                      style={{
                                        fontSize: "14px",
                                        color: "#514c67",
                                      }}
                                    />
                                  </div>

                                  <div>
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: "#444",
                                      }}
                                    >
                                      Registered Courses
                                    </div>

                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#888",
                                        marginTop: "2px",
                                      }}
                                    >
                                      Total courses associated with this student
                                    </div>
                                  </div>
                                </div>

                                <span
                                  style={{
                                    backgroundColor: "#d5d1fd",
                                    color: "#4f4a65",
                                    fontWeight: "700",
                                    fontSize: "13px",
                                    padding: "6px 12px",
                                    borderRadius: "8px",
                                    minWidth: "32px",
                                    textAlign: "center",
                                  }}
                                >
                                  {allCoursesModalData.length}
                                </span>
                              </div>

                              {/* ================= TABLE ================= */}
                              <div
                                style={{
                                  backgroundColor: "#ffffff",
                                  borderRadius: "16px",
                                  overflow: "hidden",
                                  border: "1px solid #ebe6e1",
                                  boxShadow: "0 3px 12px rgba(0,0,0,0.05)",
                                }}
                              >
                                <div className="table-responsive">
                                  <table
                                    className="table align-middle mb-0"
                                    style={{
                                      borderCollapse: "separate",
                                      borderSpacing: 0,
                                      width: "100%",
                                    }}
                                  >
                                    <thead>
                                      <tr>
                                        <th
                                          style={{
                                            backgroundColor: "#d5d1fd",
                                            color: "#000",
                                            fontWeight: "600",
                                            fontSize: "13px",
                                            padding: "14px 16px",
                                            border: "none",
                                            textAlign: "center",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          COURSE ID
                                        </th>

                                        <th
                                          style={{
                                            backgroundColor: "#d5d1fd",
                                            color: "#000",
                                            fontWeight: "600",
                                            fontSize: "13px",
                                            padding: "14px 16px",
                                            border: "none",
                                            textAlign: "left",
                                          }}
                                        >
                                          COURSE TITLE
                                        </th>

                                        <th
                                          style={{
                                            backgroundColor: "#d5d1fd",
                                            color: "#000",
                                            fontWeight: "600",
                                            fontSize: "13px",
                                            padding: "14px 16px",
                                            border: "none",
                                            textAlign: "left",
                                          }}
                                        >
                                          TEACHER
                                        </th>

                                        <th
                                          style={{
                                            backgroundColor: "#d5d1fd",
                                            color: "#000",
                                            fontWeight: "600",
                                            fontSize: "13px",
                                            padding: "14px 16px",
                                            border: "none",
                                            textAlign: "center",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          STATUS
                                        </th>
                                      </tr>
                                    </thead>

                                    <tbody>
                                      {allCoursesModalData.map(
                                        (course, index) => (
                                          <tr key={index}>
                                            {/* Course ID */}
                                            <td
                                              style={{
                                                padding: "15px 16px",
                                                textAlign: "center",
                                                fontSize: "13px",
                                                color: "#666",
                                                borderBottom:
                                                  index ===
                                                  allCoursesModalData.length - 1
                                                    ? "none"
                                                    : "1px solid #f0ece8",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  display: "inline-block",
                                                  backgroundColor: "#f3f1f7",
                                                  color: "#625d75",
                                                  padding: "5px 9px",
                                                  borderRadius: "7px",
                                                  fontSize: "12px",
                                                  fontWeight: "600",
                                                }}
                                              >
                                                {course.id}
                                              </span>
                                            </td>

                                            {/* Course Title */}
                                            <td
                                              style={{
                                                padding: "15px 16px",
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                color: "#333",
                                                borderBottom:
                                                  index ===
                                                  allCoursesModalData.length - 1
                                                    ? "none"
                                                    : "1px solid #f0ece8",
                                              }}
                                            >
                                              {course.title || "Unknown"}
                                            </td>

                                            {/* Teacher */}
                                            <td
                                              style={{
                                                padding: "15px 16px",
                                                fontSize: "14px",
                                                borderBottom:
                                                  index ===
                                                  allCoursesModalData.length - 1
                                                    ? "none"
                                                    : "1px solid #f0ece8",
                                              }}
                                            >
                                              {course.Teacher?.name ||
                                                "Unknown"}
                                            </td>

                                            {/* Status */}
                                            <td
                                              style={{
                                                padding: "15px 16px",
                                                textAlign: "center",
                                                borderBottom:
                                                  index ===
                                                  allCoursesModalData.length - 1
                                                    ? "none"
                                                    : "1px solid #f0ece8",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  display: "inline-block",
                                                  padding: "5px 11px",
                                                  borderRadius: "8px",
                                                  fontSize: "11px",
                                                  fontWeight: "600",
                                                  backgroundColor:
                                                    course.status === "enrolled"
                                                      ? "#bff1db"
                                                      : course.status ===
                                                          "pending"
                                                        ? "#f9e1be"
                                                        : "#f2c4c4",
                                                  color:
                                                    course.status === "enrolled"
                                                      ? "#285d45"
                                                      : course.status ===
                                                          "pending"
                                                        ? "#755827"
                                                        : "#7b3f3f",
                                                }}
                                              >
                                                {course.status || "Unknown"}
                                              </span>
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </>
                          ) : (
                            /* ================= EMPTY STATE ================= */
                            <div
                              className="d-flex flex-column align-items-center justify-content-center"
                              style={{
                                minHeight: "300px",
                                backgroundColor: "#ffffff",
                                borderRadius: "16px",
                                border: "1px solid #ebe6e1",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                              }}
                            >
                              <div
                                style={{
                                  width: "56px",
                                  height: "56px",
                                  borderRadius: "50%",
                                  backgroundColor: "#d5d1fd",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  marginBottom: "16px",
                                }}
                              >
                                <i
                                  className="fa-solid fa-book-open"
                                  style={{
                                    color: "#514c67",
                                    fontSize: "21px",
                                  }}
                                />
                              </div>

                              <h5
                                style={{
                                  fontWeight: "600",
                                  color: "#444",
                                  marginBottom: "6px",
                                }}
                              >
                                No Registered Courses
                              </h5>

                              <p
                                style={{
                                  color: "#777",
                                  fontSize: "13px",
                                  marginBottom: 0,
                                }}
                              >
                                This student has not registered for any courses
                                yet.
                              </p>
                            </div>
                          )}
                        </div>

                        {/* ================= FOOTER ================= */}
                        <div
                          style={{
                            padding: "14px 28px",
                            borderTop: "1px solid #e4ddd5",
                            display: "flex",
                            justifyContent: "flex-end",
                            backgroundColor: "#ffffff",
                          }}
                        >
                          <button
                            type="button"
                            className="btn"
                            onClick={() => setShowAllCoursesModal(false)}
                            style={{
                              backgroundColor: "#d5d1fd",
                              borderColor: "#d5d1fd",
                              borderRadius: "10px",
                              padding: "8px 20px",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#403b50",
                            }}
                          >
                            CLOSE
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
